// Whether a record picker offers its "create <typed name>" row (#12137).
//
// One rule for every picker that can create — the person gate on the
// interaction page, the single-kind and multi-kind gates on record fields:
// the row is offered when nothing already *is* the typed name. A name is
// taken when a result, an already-selected record, a sibling field's record
// or the picker's current value CONTAINS the typed name, nickname-aware.
// Containment runs one way only. "Scott Yuan" is not taken by a person named
// "Scott", and "John Hancockz" is not taken by "John Hancock" (#6860) — the
// typed name containing a record's name says nothing about whether the
// typed name exists. The nickname pass is what the backend's own check
// cannot do: "Dave Baker" IS taken by "David Baker" (#6295).

import type { NicknameGroups } from "../api";

export type NicknameMap = Record<string, string[]>;

// Nickname groups arrive as formal name -> nicknames. Every name in a group
// maps to every other so the expansion runs in both directions.
export const buildNicknameMap = (groups: NicknameGroups): NicknameMap => {
  const map: NicknameMap = {};
  Object.entries(groups).forEach(([formal, nicknames]) => {
    map[formal] = [...nicknames];
    nicknames.forEach((nickname) => {
      map[nickname] = [formal, ...nicknames.filter((n) => n !== nickname)];
    });
  });
  return map;
};

// A name as every spelling the nickname table allows: lower-cased, trimmed,
// the first token expanded through the table, the rest kept literal.
export const nameVariants = (
  name: string,
  nicknameMap: NicknameMap
): string[] => {
  const parts = name.toLowerCase().trim().split(/\s+/);
  const first = parts[0];
  const firstVariants = [first, ...(nicknameMap[first] ?? [])];
  if (parts.length === 1) return firstVariants;
  const rest = parts.slice(1).join(" ");
  return firstVariants.map((variant) => `${variant} ${rest}`);
};

// Does `name` — a record that exists — take the typed `term`? True when any
// spelling of the record's name contains any spelling of the term. Never the
// reverse.
export const nameTakesTerm = (
  term: string,
  name: string,
  nicknameMap: NicknameMap
): boolean => {
  const termVariants = nameVariants(term, nicknameMap);
  const nameSpellings = nameVariants(name, nicknameMap);
  return nameSpellings.some((spelling) =>
    termVariants.some((variant) => spelling.includes(variant))
  );
};

export interface CreateRowInput {
  // The typed term the results answer.
  term: string;
  // The picker can create at all (the kind is allowed, the prop is set).
  canCreate: boolean;
  // The term's results have not landed yet.
  loading: boolean;
  // Every result's name, whatever its kind.
  resultNames: string[];
  // Names already held: selected records, sibling-field records, the
  // dropdown's current value. Blank names are placeholders and ignored.
  takenNames: string[];
  // The backend's own decision, where the endpoint makes one (the person
  // gate). Gates without one pass true.
  backendAllows: boolean;
  nicknameMap: NicknameMap;
}

export const shouldOfferCreateRow = ({
  term,
  canCreate,
  loading,
  resultNames,
  takenNames,
  backendAllows,
  nicknameMap,
}: CreateRowInput): boolean => {
  const typed = term.trim();
  if (!canCreate || !typed || loading || !backendAllows) return false;
  const existing = [...takenNames, ...resultNames].filter(
    (name) => name && name.trim()
  );
  return !existing.some((name) => nameTakesTerm(typed, name, nicknameMap));
};
